import 'firebase/compat/auth';
import 'firebase/compat/firestore';
import 'firebase/compat/storage';

import { initializeApp } from 'firebase/app';
import { 
  getFirestore, 
  collection, 
  connectFirestoreEmulator,
  enableIndexedDbPersistence,
  disableNetwork,
  enableNetwork,
  doc,
  getDoc,
  query,
  where,
  getDocs
} from 'firebase/firestore';
import { 
  getAuth, 
  connectAuthEmulator 
} from 'firebase/auth';
import { 
  getStorage, 
  connectStorageEmulator 
} from 'firebase/storage';
import { 
  getFunctions, 
  connectFunctionsEmulator 
} from 'firebase/functions';
import { 
  getAnalytics, 
  isSupported as isAnalyticsSupported 
} from 'firebase/analytics';
import {
  initializeAppCheck,
  ReCaptchaV3Provider
} from 'firebase/app-check';

// Import Firebase configuration
import { 
  firebaseConfig, 
  FEATURE_FLAGS, 
  APP_CHECK_CONFIG,
  ERROR_CONFIG
} from './config/firebase.config.js';

// Environment flags
export const isDevelopment = process.env.NODE_ENV === 'development';
export const isProduction = process.env.NODE_ENV === 'production';
export const isOnline = navigator.onLine;

// Initialize Firebase App with error handling
let app;
try {
  app = initializeApp(firebaseConfig);
} catch (error) {
  console.error('Failed to initialize Firebase app:', error);
  throw new Error('Firebase initialization failed');
}

// Initialize Firestore and create references
const db = getFirestore(app);

// Define collection names with fallbacks
const COLLECTIONS = {
  users: 'users',
  streams: 'streams',
  categories: 'categories',
  conversations: 'conversations',
  messages: 'messages',
  ...(firebaseConfig.collections || {})
};

// Create collection references
const usersRef = collection(db, COLLECTIONS.users);
const streamsRef = collection(db, COLLECTIONS.streams);
const categoriesRef = collection(db, COLLECTIONS.categories);
const conversationsRef = collection(db, COLLECTIONS.conversations);
const messagesRef = collection(db, COLLECTIONS.messages);

// Initialize Auth
const auth = getAuth(app);

// Initialize Storage
const storage = getStorage(app);

// Initialize Functions
const functions = getFunctions(app);

// Initialize AppCheck and Analytics conditionally
let appCheck = null;
let analytics = null;

// Environment flags
const useEmulators = FEATURE_FLAGS.USE_EMULATORS && isDevelopment;

// Connect to emulators in development
if (useEmulators) {
  try {
    console.log('🔧 Using Firebase Emulators');
    
    if (firebaseConfig.emulators?.firestore?.enabled) {
      const { host, port } = firebaseConfig.emulators.firestore;
      connectFirestoreEmulator(db, host, port);
      console.log(`🔥 Firestore emulator connected at ${host}:${port}`);
    }
    
    if (firebaseConfig.emulators?.auth?.enabled) {
      const { host, port } = firebaseConfig.emulators.auth;
      connectAuthEmulator(auth, `http://${host}:${port}`, { disableWarnings: true });
      console.log(`🔐 Auth emulator connected at ${host}:${port}`);
    }
    
    if (firebaseConfig.emulators?.functions?.enabled) {
      const { host, port } = firebaseConfig.emulators.functions;
      connectFunctionsEmulator(functions, host, port);
      console.log(`⚡ Functions emulator connected at ${host}:${port}`);
    }
    
    if (firebaseConfig.emulators?.storage?.enabled) {
      const { host, port } = firebaseConfig.emulators.storage;
      connectStorageEmulator(storage, host, port);
      console.log(`📦 Storage emulator connected at ${host}:${port}`);
    }
  } catch (error) {
    console.error('❌ Failed to connect to Firebase emulators:', error);
  }
}

// Initialize App Check with error handling
try {
  if (APP_CHECK_CONFIG.siteKey) {
    const provider = new ReCaptchaV3Provider(APP_CHECK_CONFIG.siteKey);
    appCheck = initializeAppCheck(app, {
      provider,
      isTokenAutoRefreshEnabled: true
    });
    console.log('🛡️ Firebase AppCheck initialized');
  }
} catch (error) {
  console.error('❌ Failed to initialize AppCheck:', error);
}

// Initialize Analytics in production with error handling
if (FEATURE_FLAGS.ENABLE_ANALYTICS) {
  isAnalyticsSupported()
    .then(supported => {
      if (supported) {
        analytics = getAnalytics(app);
        console.log('📊 Firebase Analytics initialized');
      } else {
        console.log('⚠️ Firebase Analytics not supported in this environment');
      }
    })
    .catch(err => {
      console.error('❌ Analytics initialization error:', err);
    });
}

// Setup Firestore offline persistence with error handling
if (isProduction) {
  enableIndexedDbPersistence(db)
    .catch((err) => {
      if (err.code === 'failed-precondition') {
        console.warn('Multiple tabs open, persistence can only be enabled in one tab at a time.');
      } else if (err.code === 'unimplemented') {
        console.warn('The current browser does not support persistence.');
      } else {
        console.error('Failed to enable persistence:', err);
      }
    });
}

// Online/Offline handling with debouncing
let networkListenersInitialized = false;
let networkTimeout = null;

const initNetworkListeners = () => {
  if (networkListenersInitialized) return;

  const handleNetworkChange = (isOnline) => {
    if (networkTimeout) {
      clearTimeout(networkTimeout);
    }

    networkTimeout = setTimeout(async () => {
      try {
        if (isOnline) {
          console.log('🌐 Device is online');
          await enableNetwork(db);
        } else {
          console.log('📴 Device is offline');
          await disableNetwork(db);
        }
      } catch (error) {
        console.error(`Failed to ${isOnline ? 'enable' : 'disable'} network:`, error);
      }
    }, 1000); // Debounce for 1 second
  };

  window.addEventListener('online', () => handleNetworkChange(true));
  window.addEventListener('offline', () => handleNetworkChange(false));
  
  networkListenersInitialized = true;
};

// Initialize network listeners
initNetworkListeners();

/**
 * Checks Firebase connectivity status with timeout
 * @returns {Promise<{isConnected: boolean, reason: string|null, error: Error|null}>}
 */
export const checkFirebaseConnectivity = async () => {
  if (!navigator.onLine) {
    return {
      isConnected: false,
      reason: 'DEVICE_OFFLINE',
      error: null
    };
  }
  
  try {
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => reject(new Error('Connection timeout')), ERROR_CONFIG.TIMEOUT);
    });

    const connectionPromise = enableNetwork(db).then(() => ({
      isConnected: true,
      reason: null,
      error: null
    }));

    return await Promise.race([connectionPromise, timeoutPromise]);
  } catch (error) {
    return {
      isConnected: false,
      reason: 'FIREBASE_ERROR',
      error
    };
  }
};

/**
 * Retries Firebase connection with exponential backoff
 * @returns {Promise<boolean>} Success status
 */
export const retryFirebaseConnection = async () => {
  const { MAX_RETRIES, RETRY_DELAY } = ERROR_CONFIG;
  let currentRetry = 0;
  
  while (currentRetry < MAX_RETRIES) {
    try {
      const { isConnected, error } = await checkFirebaseConnectivity();
      
      if (isConnected) {
        console.log('🔄 Firebase connection restored');
        return true;
      }
      
      currentRetry++;
      const delay = RETRY_DELAY * Math.pow(2, currentRetry - 1);
      console.log(`⏳ Retrying Firebase connection in ${delay}ms (attempt ${currentRetry}/${MAX_RETRIES})`);
      await new Promise(resolve => setTimeout(resolve, delay));
    } catch (error) {
      console.error('❌ Error during connection retry:', error);
      currentRetry++;
    }
  }
  
  console.error('❌ Failed to restore Firebase connection after', MAX_RETRIES, 'attempts');
  return false;
};

/**
 * Returns initialized Firebase services
 * @returns {Object} Firebase services
 */
export const getServices = () => ({
  app,
  db,
  auth,
  storage,
  functions,
  appCheck,
  analytics,
  usersRef,
  streamsRef,
  categoriesRef,
  conversationsRef,
  messagesRef,
  checkFirebaseConnectivity,
  retryFirebaseConnection
});

export const forceNetworkReset = async () => {
  try {
    await disableNetwork(db);
    await new Promise(resolve => setTimeout(resolve, 1000));
    await enableNetwork(db);
    return true;
  } catch (error) {
    console.error('Failed to reset network:', error);
    return false;
  }
};

export const checkOnlineStatus = () => ({
  isOnline: navigator.onLine,
  timestamp: new Date().toISOString()
});

export const getUserByUsername = async (username) => {
  try {
    const q = query(usersRef, where('username', '==', username));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs[0]?.data() || null;
  } catch (error) {
    console.error('Error fetching user by username:', error);
    throw error;
  }
};

export const getUserById = async (userId) => {
  try {
    const userDoc = await getDoc(doc(usersRef, userId));
    return userDoc.exists() ? userDoc.data() : null;
  } catch (error) {
    console.error('Error fetching user by ID:', error);
    throw error;
  }
};

// Export individual services for backward compatibility
export {
  app,
  db,
  auth,
  storage,
  functions,
  appCheck,
  analytics,
  usersRef,
  streamsRef,
  categoriesRef,
  conversationsRef,
  messagesRef,
  doc,
  getDoc,
  collection,
  query,
  where,
  getDocs,
  getFirestore,
  getFunctions,
  getAuth,
  getStorage,
  getAnalytics
}; 